/**
 * Utility functions for handling Zebra printer operations
 */

// Define Zebra printer vendor IDs - add more if needed
const ZEBRA_VENDOR_IDS = [
    0x0a5f, // Common Zebra printer vendor ID
    0x0a5e, // Alternative Zebra printer vendor ID
];

/**
 * Check if WebUSB is supported by the browser
 * @returns {boolean}
 */
export const isWebUSBSupported = () => {
    return navigator && navigator.usb && typeof navigator.usb.getDevices === 'function';
};

/**
 * Request USB permissions if needed
 * @returns {Promise<boolean>} true if permissions granted
 */
export const requestUSBPermission = async () => {
    if (!isWebUSBSupported()) {
        throw new Error('WebUSB is not supported in this browser. Please use Chrome or Edge.');
    }

    try {
        const device = await navigator.usb.requestDevice({
            filters: ZEBRA_VENDOR_IDS.map(id => ({ vendorId: id }))
        });
        return !!device;
    } catch (err) {
        if (err.name === 'NotFoundError') {
            throw new Error('No compatible printer found. Please make sure your Zebra printer is connected.');
        } else if (err.name === 'SecurityError') {
            throw new Error('USB permission denied. Please allow access to the printer when prompted.');
        }
        throw err;
    }
};

/**
 * Try to connect to a Zebra printer
 * @returns {Promise<USBDevice>} Connected printer device
 */
export const connectZebraPrinter = async () => {
    if (!isWebUSBSupported()) {
        throw new Error('WebUSB is not supported in this browser. Please use Chrome or Edge.');
    }

    // First try to get already paired printers
    try {
        const devices = await navigator.usb.getDevices();
        const zebraPrinter = devices.find(device =>
            ZEBRA_VENDOR_IDS.includes(device.vendorId)
        );

        if (zebraPrinter) {
            try {
                if (zebraPrinter.opened) {
                    await zebraPrinter.close();
                }
                await zebraPrinter.open();
                if (!zebraPrinter.configuration) {
                    await zebraPrinter.selectConfiguration(1);
                }
                await zebraPrinter.claimInterface(0);
                return zebraPrinter;
            } catch (err) {
                console.warn('Error with existing printer, trying to request new device:', err);
                // If there's an error with the existing printer, try requesting a new one
                await requestUSBPermission();
                return await connectZebraPrinter(); // Retry connection
            }
        }
    } catch (err) {
        console.log('No previously paired printer found, requesting new device');
    }

    // If no paired printer found or failed to connect, request a new one
    try {
        await requestUSBPermission();
        const devices = await navigator.usb.getDevices();
        const printer = devices.find(device =>
            ZEBRA_VENDOR_IDS.includes(device.vendorId)
        );

        if (!printer) {
            throw new Error('No printer found after permission granted');
        }

        await printer.open();
        await printer.selectConfiguration(1);
        await printer.claimInterface(0);
        return printer;
    } catch (err) {
        if (err.name === 'NotFoundError') {
            throw new Error('No compatible printer found. Please check your connection.');
        } else if (err.name === 'SecurityError') {
            throw new Error('USB access denied. Please allow access when prompted and try again.');
        }
        throw new Error('Failed to connect to printer: ' + err.message);
    }
};

/**
 * Print ZPL data to a Zebra printer
 * @param {string} zpl The ZPL code to print
 * @returns {Promise<void>}
 */
export const printZPL = async (zpl) => {
    if (!zpl) {
        throw new Error('No ZPL data provided');
    }

    let device;
    try {
        // Check WebUSB support first
        if (!isWebUSBSupported()) {
            throw new Error('WebUSB is not supported in this browser. Please use Chrome or Edge.');
        }

        device = await connectZebraPrinter();

        // Convert ZPL to proper format
        const encoder = new TextEncoder();
        const data = encoder.encode(zpl);

        // Send data to printer
        await device.transferOut(1, data);
        return true;
    } catch (err) {
        // Provide more specific error messages
        if (err.name === 'SecurityError') {
            throw new Error('USB access denied. Please allow access when prompted and try again.');
        } else if (err.name === 'NotFoundError') {
            throw new Error('Printer not found. Please check if it is properly connected.');
        }
        throw err;
    } finally {
        // Always close the connection
        if (device && device.opened) {
            try {
                await device.close();
            } catch (err) {
                console.error('Error closing printer connection:', err);
            }
        }
    }
};

export default {
    connectZebraPrinter,
    printZPL
};